import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import {
  type CreateAsyncExportApiResponse,
  type CreateAsyncExportRequest,
  type CreateSyncExportRequest,
  EntReportType,
  type GetEntReportPreviewApiResponse,
  type GetEntReportPreviewRequest,
  type GetExportStatusApiResponse,
  type GetExportStatusData,
  type GetReportColumnsApiResponse,
} from '../types';

export const entReportsApi = createApi({
  reducerPath: EntReportType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

export const entReportsApiEndpoints = entReportsApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportColumns: builder.query<unknown, string>({
      query: (reportType) => ({
        url: '/api/sodms/view/columns',
        isECMv6: true,
        params: {
          view: 'report_columns',
          reportType,
        },
      }),
      transformResponse: (response: GetReportColumnsApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        throw new Error(response.message || 'Failed to fetch report columns');
      },
    }),
    getEntReportPreview: builder.mutation<
      unknown,
      { payload: GetEntReportPreviewRequest; reportType: string }
    >({
      query: ({ payload, reportType }) => ({
        url: '/api/sodms/sod-violations/reports/preview',
        method: 'POST',
        isECMv6: true,
        data: payload,
        params: {
          reportType,
        },
      }),
      transformResponse: (response: GetEntReportPreviewApiResponse) => {
        if (response.message === 'No record found') {
          return 'No record found';
        }

        if (response.status === 200) {
          return response.data;
        }

        return null;
      },
    }),
    createAsyncExport: builder.mutation<unknown, CreateAsyncExportRequest>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/reports/exports/async',
        method: 'POST',
        isECMv6: true,
        data: payload,
      }),
      transformResponse: (response: unknown) => {
        if (
          response &&
          typeof response === 'object' &&
          'status' in response &&
          'data' in response
        ) {
          const apiResponse = response as CreateAsyncExportApiResponse;

          if (apiResponse.status === 200) {
            return apiResponse.data;
          }

          throw new Error(
            apiResponse.message || 'Failed to create async export'
          );
        }

        throw new Error('Invalid response format');
      },
    }),
    getExportStatus: builder.query<GetExportStatusData, string>({
      query: (userId) => ({
        url: `/api/sodms/sod-violations/reports/exports/status?userId=${userId}`,
        method: 'GET',
        isECMv6: true,
      }),
      transformResponse: (response: unknown): GetExportStatusData => {
        if (
          response &&
          typeof response === 'object' &&
          'status' in response &&
          'data' in response
        ) {
          const apiResponse = response as GetExportStatusApiResponse;

          if (apiResponse.status === 200) {
            return apiResponse.data;
          }

          throw new Error(
            apiResponse.message || 'Failed to fetch export status'
          );
        }

        throw new Error('Invalid response format for export status');
      },
    }),
    downloadExport: builder.query<Blob, string>({
      query: (exportId) => ({
        url: `/api/sodms/sod-violations/reports/exports/${exportId}/download`,
        method: 'GET',
        isECMv6: true,
        config: { responseType: 'blob' },
      }),
      transformResponse: (response: unknown) => response as Blob,
    }),
    acknowledgeExport: builder.mutation<void, string>({
      query: (exportId) => ({
        url: `/api/sodms/sod-violations/reports/exports/${exportId}/acknowledge`,
        method: 'PUT',
        isECMv6: true,
      }),
      transformResponse: () => undefined,
    }),
    generateSyncExport: builder.mutation<unknown, CreateSyncExportRequest>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/reports/exports/sync',
        method: 'POST',
        isECMv6: true,
        data: payload,
        config: { responseType: 'blob' },
      }),
      transformResponse: (response: unknown) => response as Blob,
    }),
  }),
});

export const {
  useLazyGetReportColumnsQuery,
  useGetEntReportPreviewMutation,
  useCreateAsyncExportMutation,
  useLazyGetExportStatusQuery,
  useLazyDownloadExportQuery,
  useAcknowledgeExportMutation,
  useGenerateSyncExportMutation,
} = entReportsApiEndpoints;
